import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TableColumn } from '@maersk-global/mds-components-core-table/types';
import {
  CarrierConfigurationDetailDto,
  CarrierConfigurationListItemDto,
  CarrierConfigurationListResponse,
  CarrierConfigurationMappingDto,
  CarrierConfigurationVersionItemDto,
  CarrierListResponse,
  CreateCarrierConfigurationRequest,
  ExecuteTestFlowRequest,
  ExecuteTestFlowResponse,
  SavePhaseMappingsRequest,
  TrackingPollTargetEventDto,
  TrackingPollTargetListResponse,
  UpsertCarrierConfigurationMappingRequest,
  UpdateCarrierConfigurationRequest,
} from '@carrier-configurations/models/carrier-configuration.model';
import { environment } from '@src/environments/environment';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CarrierConfigurationsService {
  private readonly endpoint: string = environment.config.CIRE_SERVICE_URL;

  tableColumns: TableColumn[] = [
    { id: 'carrierCode', label: 'Carrier', sortDisabled: true, width: '22%' },
    { id: 'activeVersion', label: 'Active version', sortDisabled: true, width: '28%' },
    { id: 'updatedAtUtc', label: 'Updated at', sortDisabled: true, width: '35%' },
    { id: 'id', label: 'Actions', sortDisabled: true, rowClickDisabled: true, width: '15%' },
  ];

  constructor(private http: HttpClient) {}

  readonly mappingKeysByPhase: Record<string, string[]> = {
    auth: [],
    label: [
      'mappings/label.validation.json',
      'mappings/label.request.home.json',
      'mappings/label.request.pudo.json',
      'mappings/label.print.request.json',
      'mappings/label.response.json',
      'mappings/place-order.request.json',
    ],
    tracking: ['mappings/tracking.request.json', 'mappings/tracking.response.json'],
    pudo: ['mappings/pudo.list.request.json', 'mappings/pudo.response.json'],
  };

  getTableColumns(): TableColumn[] {
    return this.tableColumns;
  }

  list(
    pageNumber: number,
    pageSize: number,
    carrierCode?: string,
    isActive?: boolean
  ): Observable<CarrierConfigurationListResponse> {
    let url = `${this.endpoint}/api/v1/cire/configurations?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (carrierCode && carrierCode.trim().length > 0) {
      url += `&carrierCode=${encodeURIComponent(carrierCode.trim())}`;
    }

    if (isActive !== undefined) {
      url += `&isActive=${isActive}`;
    }

    return this.http.get<CarrierConfigurationListResponse>(url);
  }

  getById(id: number): Observable<CarrierConfigurationDetailDto> {
    return this.http.get<CarrierConfigurationDetailDto>(
      `${this.endpoint}/api/v1/cire/configurations/${id}`
    );
  }

  create(
    payload: CreateCarrierConfigurationRequest
  ): Observable<CarrierConfigurationDetailDto> {
    return this.http.post<CarrierConfigurationDetailDto>(
      `${this.endpoint}/api/v1/cire/configurations`,
      payload
    );
  }

  update(
    id: number,
    payload: UpdateCarrierConfigurationRequest
  ): Observable<CarrierConfigurationDetailDto> {
    return this.http.put<CarrierConfigurationDetailDto>(
      `${this.endpoint}/api/v1/cire/configurations/${id}`,
      payload
    );
  }

  activate(id: number): Observable<void> {
    return this.http.post<void>(
      `${this.endpoint}/api/v1/cire/configurations/${id}/activate`,
      {}
    );
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(
      `${this.endpoint}/api/v1/cire/configurations/${id}`
    );
  }

  listVersionsByCarrier(carrierCode: string): Observable<CarrierConfigurationVersionItemDto[]> {
    return this.http.get<CarrierConfigurationVersionItemDto[]>(
      `${this.endpoint}/api/v1/cire/configurations/carrier/${encodeURIComponent(carrierCode)}`
    );
  }

  listMappings(configurationId: number): Observable<CarrierConfigurationMappingDto[]> {
    return this.http.get<CarrierConfigurationMappingDto[]>(
      `${this.endpoint}/api/v1/cire/configurations/${configurationId}/mappings`
    );
  }

  getMapping(
    configurationId: number,
    mappingKey: string
  ): Observable<CarrierConfigurationMappingDto> {
    const encoded = encodeURIComponent(mappingKey);
    return this.http.get<CarrierConfigurationMappingDto>(
      `${this.endpoint}/api/v1/cire/configurations/${configurationId}/mappings/${encoded}`
    );
  }

  upsertMapping(
    configurationId: number,
    mappingKey: string,
    payload: UpsertCarrierConfigurationMappingRequest
  ): Observable<void> {
    const encoded = encodeURIComponent(mappingKey);
    return this.http.put<void>(
      `${this.endpoint}/api/v1/cire/configurations/${configurationId}/mappings/${encoded}`,
      payload
    );
  }

  savePhaseMappings(
    configurationId: number,
    payload: SavePhaseMappingsRequest
  ): Observable<CarrierConfigurationDetailDto> {
    return this.http.post<CarrierConfigurationDetailDto>(
      `${this.endpoint}/api/v1/cire/configurations/${configurationId}/phase-save`,
      payload
    );
  }

  listCarriers(
    search: string | undefined,
    pageNumber: number,
    pageSize: number
  ): Observable<CarrierListResponse> {
    let url = `${this.endpoint}/api/v1/cire/carriers?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (search && search.trim().length > 0) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }

    return this.http.get<CarrierListResponse>(url);
  }

  executeTestFlow(payload: ExecuteTestFlowRequest): Observable<ExecuteTestFlowResponse> {
    return this.http.post<ExecuteTestFlowResponse>(
      `${this.endpoint}/api/v1/cire/test-execute`,
      payload
    );
  }

  listTrackingPollTargets(
    carrierCode: string,
    pageNumber: number,
    pageSize: number,
    search?: string
  ): Observable<TrackingPollTargetListResponse> {
    let url = `${this.endpoint}/api/v1/cire/configurations/carrier/${encodeURIComponent(carrierCode)}/tracking-poll-targets?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (search && search.trim().length > 0) {
      url += `&search=${encodeURIComponent(search.trim())}`;
    }
    return this.http.get<TrackingPollTargetListResponse>(url);
  }

  listTrackingPollTargetEvents(targetId: number): Observable<TrackingPollTargetEventDto[]> {
    return this.http.get<TrackingPollTargetEventDto[]>(
      `${this.endpoint}/api/v1/cire/configurations/tracking-poll-targets/${targetId}/tracking-events`
    );
  }

  mapForTable(
    list: CarrierConfigurationListItemDto[]
  ): CarrierConfigurationListItemDto[] {
    return list.map((item) => ({
      ...item,
      updatedAtUtc: formatUpdatedAtUtcMinute(item.updatedAtUtc),
    }));
  }
}

/** Display UTC timestamp to minute precision (YYYY-MM-DD HH:mm UTC). */
function formatUpdatedAtUtcMinute(iso: string | undefined): string {
  if (!iso) {
    return '';
  }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return iso;
  }
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}
